import { Box, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import {
  IconBrandMastercard,
  IconBrandVisa,
  IconCreditCard,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import Button from "../../../../../components/ui/Button";
import { LoadingSpinner } from "../../../../../components/ui/LoadingSpinner";
import Text from "../../../../../components/ui/Text";
import { useToast } from "../../../../../contexts/ToastContext";
import apiService from "../../../../../services/api";
import { DeletePaymentDialog } from "./parts";

export const PaymentMethods = () => {
  const { showToast } = useToast();

  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const getCardIcon = (brand) => {
    const iconProps = { size: 24, stroke: 1.5 };

    switch (brand?.toLowerCase()) {
      case "visa":
        return <IconBrandVisa {...iconProps} />;
      case "mastercard":
        return <IconBrandMastercard {...iconProps} />;
      default:
        return <IconCreditCard {...iconProps} />;
    }
  };

  const fetchPaymentMethods = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.fetchListPaymentMethods();
      if (response.success) {
        setSavedPaymentMethods(response.data);
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDeleteDialog = (method) => {
    setMethodToDelete(method);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setMethodToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!methodToDelete) return;

    setIsDeleting(true);
    try {
      const response = await apiService.deletePaymentMethod(methodToDelete.id);
      if (response.success) {
        // Refresh payment methods list
        fetchPaymentMethods();
        showToast(response.message, "success");
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsDeleting(false);
      handleCloseDeleteDialog();
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  return (
    <Box sx={{ mb: 4 }}>
      <Text
        variant="paragraph"
        sx={{ mb: 2, fontSize: "16px", fontWeight: 700 }}
      >
        Payment methods
      </Text>

      {isLoading && <LoadingSpinner sx={{ py: 2 }} />}

      {!isLoading && savedPaymentMethods.length === 0 && (
        <Text
          component="p"
          variant="paragraph2"
          sx={{ color: "#666", my: 4, textAlign: "center", fontSize: "16px" }}
        >
          No saved payment methods.
        </Text>
      )}

      <List
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          mt: 2,
        }}
      >
        {savedPaymentMethods.map((method) => (
          <ListItem
            key={method.id}
            sx={{
              border: "1px solid #e5e5e5",
              borderRadius: "8px",
              px: 2,
              py: 0,
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {getCardIcon(method.card.brand)}
            </ListItemIcon>
            <ListItemText
              primary={method.card.brand}
              secondary={`Ending ${method.card.last4}`}
              sx={{
                "& .MuiTypography-root": {
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "#000",
                  textTransform: "capitalize",
                },
                "& .MuiListItemText-secondary": {
                  fontSize: "14px",
                  lineHeight: 1,
                  color: "#666",
                },
              }}
            />
            <Button
              variant="link"
              onClick={() => handleOpenDeleteDialog(method)}
              sx={{ color: "#FF4D4F", fontSize: "14px" }}
            >
              Delete
            </Button>
          </ListItem>
        ))}
      </List>

      {/* <Button
        variant="link"
        onClick={handleAddPaymentMethod}
        sx={{ color: "#1890FF", fontSize: "16px", p: 0 }}
      >
        Add a payment method
      </Button> */}

      {/* Delete Confirmation Dialog */}
      <DeletePaymentDialog
        isOpen={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </Box>
  );
};
